import { useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MessageSquarePlus, Bug, Lightbulb, X } from "lucide-react";
import { toast } from "sonner";

/**
 * "Rapporter feil/forbedring"-knapp + dialog. Brukar kan velje kategori
 * (feil/forbedring), legge inn ein valgfri tittel og ein beskrivelse. Postar
 * til `POST /api/feedback` og lagrar med brukerens user_id, email og kva side
 * dei var på når dei klikka.
 */
export default function FeedbackButton({ page = "" }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("issue");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!message.trim()) { toast.error("Skriv inn ein beskrivelse"); return; }
    setSaving(true);
    // Capture technical context so admins can reproduce mobile/browser-specific
    // bugs without having to ask the user 5 follow-up questions.
    const meta = {};
    try {
      meta.viewport = `${window.innerWidth}x${window.innerHeight}`;
      meta.dpr = window.devicePixelRatio || 1;
      meta.ua = navigator.userAgent;
      meta.platform = navigator.platform || "";
      meta.lang = navigator.language || "";
      meta.online = navigator.onLine;
      meta.pwa = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
    } catch { /* ignore */ }
    try {
      await api.submitFeedback({
        category,
        title: title.trim(),
        message: message.trim(),
        page: page || (typeof window !== "undefined" ? window.location.pathname : ""),
        meta,
      });
      toast.success("Takk! Tilbakemeldinga er sendt.");
      setOpen(false);
      setTitle("");
      setMessage("");
      setCategory("issue");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikkje sende — prøv igjen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full bg-navy-50 hover:bg-navy-100 border border-navy-200 px-3 py-1.5 text-xs font-semibold text-navy-800 transition-colors"
        data-testid="feedback-open-btn"
        title="Send feilmelding eller forbedringsforslag"
      >
        <MessageSquarePlus className="h-3.5 w-3.5" />
        <span>Rapporter feil / forbedring</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <MessageSquarePlus className="h-5 w-5 text-navy-800" /> Rapporter feil eller forbedring
            </DialogTitle>
            <DialogDescription className="text-xs">
              Vi les alle tilbakemeldingar og brukar dei til å gjere appen betre.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2 block">Kategori</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setCategory("issue")}
                  className={`flex flex-col items-center gap-1 px-3 py-3 rounded-lg border-2 transition-colors ${category === "issue" ? "border-red-300 bg-red-50 text-red-800" : "border-stone-200 bg-white text-stone-600 hover:border-stone-300"}`}
                  data-testid="feedback-cat-issue"
                >
                  <Bug className="h-4 w-4" />
                  <span className="text-xs font-semibold">Feil / bug</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCategory("improvement")}
                  className={`flex flex-col items-center gap-1 px-3 py-3 rounded-lg border-2 transition-colors ${category === "improvement" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-stone-200 bg-white text-stone-600 hover:border-stone-300"}`}
                  data-testid="feedback-cat-improvement"
                >
                  <Lightbulb className="h-4 w-4" />
                  <span className="text-xs font-semibold">Forbedring</span>
                </button>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1 block">Tittel (valgfri)</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={category === "issue" ? "Kort beskrivelse av feilen…" : "Kort beskrivelse av forslaget…"}
                maxLength={200}
                className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none"
                data-testid="feedback-title-input"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1 block">Beskrivelse *</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={category === "issue"
                  ? "Kva skjer? Kva forventa du? Steg for å gjenta…"
                  : "Kva kunne vore betre? Korleis ser det ut i hovudet ditt?"}
                rows={5}
                maxLength={4000}
                className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none resize-none"
                data-testid="feedback-message-input"
                autoFocus
              />
              <div className="text-[10px] text-stone-400 text-right mt-0.5">{message.length} / 4000</div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setOpen(false)}
                disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-1 rounded-md border border-stone-200 px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50"
                data-testid="feedback-cancel-btn"
              >
                <X className="h-4 w-4" /> Avbryt
              </button>
              <button
                onClick={submit}
                disabled={saving || !message.trim()}
                className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-navy-800 hover:bg-navy-900 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                data-testid="feedback-submit-btn"
              >
                {saving ? "Sender…" : "Send"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
